import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, Typography } from "@mui/material";
import { removeAuditAccount, removeAuditFramework, unwrapDelete } from "../../../../helpers/auditHub_helper";
import { frameworkLabel } from "../utils";
import { AH_ACCENT, AH_TEXT, AH_WHITE, auditHubPageSx } from "../theme";

const DECIDED_CODE = "SCOPE_HAS_DECIDED_CONTROLS";

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const emphasis = (text) => (
  <Box component="span" sx={{ fontWeight: 600 }}>
    {text}
  </Box>
);

/**
 * `target` is `{ account }` to remove an account with all of its frameworks,
 * or `{ account, scope }` to remove one framework from that account.
 */
const RemoveScopeDialog = ({ auditId, target, open, onClose, onRemoved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [decided, setDecided] = useState("");

  const account = target?.account;
  const scope = target?.scope;
  const isFramework = Boolean(scope);

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setError("");
    setDecided("");
  }, [open, account?.auditAccountId, scope?.auditScopeId]);

  const accountLabel = account?.accountName || account?.accountNumber || "this account";
  const scopeCount = Array.isArray(account?.scopes) ? account.scopes.length : 0;
  const title = isFramework ? "Remove Framework" : "Remove Account";

  const confirm = async () => {
    if (!auditId || !account?.auditAccountId || saving) return;
    const addonId = scope?.framework?.complianceAddonId;
    if (isFramework && addonId == null) return;
    const force = Boolean(decided);
    setSaving(true);
    setError("");
    try {
      const result = unwrapDelete(
        isFramework
          ? await removeAuditFramework(auditId, account.auditAccountId, addonId, { force })
          : await removeAuditAccount(auditId, account.auditAccountId, { force })
      );
      onRemoved({ account, scope, result });
    } catch (err) {
      if (!force && err?.data?.error?.code === DECIDED_CODE) {
        setDecided(err.message || "Some controls here already have a verdict.");
      } else {
        setError(err?.message || "Unable to remove it from this assignment.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="remove-scope-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 600 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography id="remove-scope-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: saving ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
          {isFramework ? (
            <>
              Are you sure you want to remove {emphasis(frameworkLabel(scope.framework))} from {emphasis(accountLabel)}?
            </>
          ) : (
            <>
              Are you sure you want to remove {emphasis(accountLabel)}
              {scopeCount ? ` and its ${scopeCount} framework${scopeCount === 1 ? "" : "s"}` : ""} from this assignment?
            </>
          )}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 1 }}>
          {isFramework
            ? "Its controls, answers, evidence, comments and tasks are deleted. The account's other frameworks stay. This cannot be undone."
            : "Its controls, answers, evidence, comments and tasks are deleted. Its scan history stays available. This cannot be undone."}
        </Typography>
        {decided ? (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: "8px", fontSize: "0.8125rem", alignItems: "center" }}>
            {decided} Select Remove anyway to delete those decisions as well.
          </Alert>
        ) : null}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          onClick={confirm}
          disabled={saving || !account?.auditAccountId}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#dc2626",
            color: AH_WHITE,
            "&:hover": { backgroundColor: "#b91c1c", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#dc2626", color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {saving ? "Removing" : decided ? "Remove anyway" : "Remove"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default RemoveScopeDialog;
